export const keyTypes = {
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',
  Enter: 'Enter',
  Space: 'Space',
  Escape: 'Escape',
  Tab: 'Tab',
};

/**
 * Moves focus between elements when a specific key is pressed (arrow navigation).
 * @param container the container element holding the focusable elements
 * @param key the key that was pressed
 * @param forwardKey the key to move focus forward (e.g. ArrowRight)
 * @param backwardKey the key to move focus backward (e.g. ArrowLeft)
 * @returns nothing, but sets the focus to the next or previous focusable element
 */
export function navigate(
  container: HTMLElement,
  key: string,
  forwardKey: string,
  backwardKey: string,
  cssSelector: string,
): void {
  const items = Array.from(container.querySelectorAll<HTMLElement>(cssSelector)).filter(
    (el) => el.offsetParent !== null,
  );

  const active = document.activeElement as HTMLElement;
  const index = items.indexOf(active);
  if (index === -1) return;

  if (key === forwardKey) {
    const next = (index + 1) % items.length;
    items[next].focus();
  } else if (key === backwardKey) {
    const prev = (index - 1 + items.length) % items.length;
    items[prev].focus();
  }
}

/**
 * Get all elements in the container that can receive focus via keyboard navigation.
 * @param container The container element holding the focusable elements.
 * @returns An array of focusable elements in the container.
 */
export function getKeyboardFocusableElements(
  container: HTMLDivElement,
  ignoreHidden = false,
  cssSelector?: string,
): Array<HTMLElement> {
  const focusableSelectors = cssSelector
    ? [`.${cssSelector}`]
    : ['a[href]:not([tabindex="-1"])', 'button:not([disabled]):not([tabindex="-1"])'];

  return Array.from<HTMLElement>(
    container?.querySelectorAll<HTMLElement>(focusableSelectors.join(',')),
  ).filter((el) => {
    if (ignoreHidden) return el;
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

/**
 * Gets the previous sibling element of the given element.
 * @param element The element from which to get the previous sibling.
 * @returns The previous sibling element if it exists, otherwise null.
 */
export const focusPreviousSiblingElement = (element: HTMLElement): void => {
  const previousSibling = element.previousElementSibling;
  if (previousSibling) {
    (previousSibling as HTMLElement).focus();
  }
};

export const getNextSiblingElement = (element: HTMLElement): HTMLElement | null => {
  const nextSibling = element.nextElementSibling;
  if (nextSibling) {
    return nextSibling as HTMLElement;
  }
  return null;
};

export const trapFocus = (focusableElements: HTMLElement[], e: KeyboardEvent): void => {
  if (!focusableElements.length) return;
  if (e.key !== 'Tab') return;
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];
  const activeEl = document.activeElement as HTMLElement;

  if (e.shiftKey && activeEl === firstFocusableElement) {
    e.preventDefault();
    lastFocusableElement.focus();
  } else if (!e.shiftKey && activeEl === lastFocusableElement) {
    e.preventDefault();
    firstFocusableElement.focus();
  }
};
